import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { listTrending, listHeatmap } from "@/lib/trends.functions";
import { formatGBP } from "@/lib/format";
import { socialImageMeta, breadcrumbLd } from "@/lib/seo";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/trends")({
  head: () => ({
    meta: [
      { title: "Card market trends — BidNova Auctions" },
      { name: "description", content: "Live trends from real BidNova sales: top-moving cards and category activity for graded Pokémon cards." },
      { property: "og:title", content: "Card market trends — BidNova Auctions" },
      { property: "og:description", content: "Real trends from BidNova sales — no fabricated external data." },
      { property: "og:url", content: "https://bidnovaauctions.uk/trends" },
      { property: "og:type", content: "website" },
      ...socialImageMeta(),
      { name: "twitter:title", content: "Card market trends — BidNova Auctions" },
      { name: "twitter:description", content: "Real trends from BidNova sales — no fabricated external data." },
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/trends" }],
    scripts: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Market Trends", path: "/trends" }])],
  }),
  component: TrendsPage,
  errorComponent: ({ error }) => <div className="p-6 text-rose-400">{(error as Error).message}</div>,
  notFoundComponent: TrendsNotFound,
});

function TrendsNotFound() {
  const { t } = useTranslation("marketing");
  return <div className="p-6">{t("trends.notFound", "Not found.")}</div>;
}

function TrendsPage() {
  const { t } = useTranslation("marketing");
  const [win, setWin] = useState<"24h" | "7d" | "30d">("7d");
  const trendFn = useServerFn(listTrending);
  const heatFn = useServerFn(listHeatmap);
  const trending = useQuery({ queryKey: ["trending", win], queryFn: () => trendFn({ data: { time_window: win, limit: 20 } }) });
  const heat = useQuery({ queryKey: ["heat", win], queryFn: () => heatFn({ data: { time_window: win } }) });

  return (
    <div className="container max-w-5xl py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("trends.title", "Market Trends")}</h1>
        <p className="text-sm text-muted-foreground">{t("trends.subtitle", "Derived from real BidNova sales only. Advisory — not a valuation.")}</p>
      </div>
      <div className="flex gap-2">
        {(["24h", "7d", "30d"] as const).map((w) => (
          <button
            key={w}
            onClick={() => setWin(w)}
            className={`px-3 py-1 rounded-md text-sm border ${win === w ? "border-gold text-gold" : "border-border/60 text-muted-foreground"}`}
          >
            {w}
          </button>
        ))}
      </div>

      <section>
        <h2 className="text-lg font-medium mb-3">{t("trends.trendingCards", "Trending cards")}</h2>
        <div className="grid gap-2 sm:grid-cols-2">
          {((trending.data ?? []) as Array<{ card_key: string; card_name: string | null; card_set: string | null; sample_count: number; avg_price_pence: number | null }>).map((r) => (
            <div key={r.card_key} className="rounded-lg border border-border/40 bg-surface-2/40 p-3">
              <div className="text-sm font-medium">{r.card_name}</div>
              <div className="text-xs text-muted-foreground">
                {r.card_set ?? t("trends.unknownSet", "unknown set")} · {t("trends.salesCount", "{count} sales", { count: r.sample_count })} · {t("trends.averagePrefix", "avg {price}", { price: r.avg_price_pence ? formatGBP(r.avg_price_pence) : "—" })}
              </div>
            </div>
          ))}
          {(trending.data ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground col-span-2">
              {t("trends.noTrendData", "No trend data yet for this window. Trends appear as real BidNova sales accumulate.")}
            </p>
          )}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-medium mb-3">{t("trends.categoryActivity", "Category activity")}</h2>
        <div className="grid gap-2 sm:grid-cols-3">
          {((heat.data ?? []) as Array<{ card_game: string; region: string; sample_count: number; activity_score: number }>).map((h) => (
            <div key={`${h.card_game}-${h.region}`} className="rounded-lg border border-border/40 bg-surface-2/40 p-3">
              <div className="text-sm font-medium capitalize">{h.card_game}</div>
              <div className="text-xs text-muted-foreground">{h.region} · {t("trends.salesCount", "{count} sales", { count: h.sample_count })}</div>
              <div className="mt-2 h-2 rounded bg-background overflow-hidden">
                <div className="h-full bg-gold" style={{ width: `${Math.min(100, Number(h.activity_score))}%` }} />
              </div>
            </div>
          ))}
          {(heat.data ?? []).length === 0 && <p className="text-sm text-muted-foreground col-span-3">{t("trends.noActivityData", "No activity yet for this window.")}</p>}
        </div>
      </section>
    </div>
  );
}
